import { type ValueEvent } from '@adornis/base/utilTypes.js';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import type { ColumnDefinition } from '@adornis/data-view/ColumnDefinition.js';
import type { DataViewOptions } from '@adornis/data-view/client/data-view-options.js';
import { View } from '@adornis/data-view/db/view.js';
import { BooleanAspect } from '@adornis/filter/aspect-schemas/BooleanAspect.js';
import '@chancenwerk/components/chancen-component-card-title.js';
import { GroupType } from '@chancenwerk/db/enums/enums.js';
import { ChancenwerkUserFilter } from '@chancenwerk/db/filter/ChancenwerkUserFilter.js';
import { StudentFilter } from '@chancenwerk/db/filter/StudentFilter.js';
import '@chancenwerk/design/components/cr-button.js';
import '@chancenwerk/design/components/cr-campus-loader.js';
import '@chancenwerk/design/components/cr-card.js';
import '@chancenwerk/design/components/cr-flex.js';
import '@chancenwerk/design/components/cr-input.js';
import '@chancenwerk/design/components/cr-link.js';
import '@chancenwerk/design/components/fonts.js';
import { UserAPI } from '@chancenwerk/master-data-management/users/api/UserAPI.js';
import type { ChancenwerkUser } from '@chancenwerk/master-data-management/users/entities/ChancenwerkUser.js';
import type { ExternerMitarbeiter } from '@chancenwerk/master-data-management/users/entities/ExternerMitarbeiter.js';
import { Mitarbeiter } from '@chancenwerk/master-data-management/users/entities/Mitarbeiter.js';
import { Student } from '@chancenwerk/master-data-management/users/entities/Student.js';
import type { VerwaltungsMitarbeiter } from '@chancenwerk/master-data-management/users/entities/VerwaltungsMitarbeiter.js';
import { html } from 'lit';
import { customElement, state } from 'lit/decorators.js';

@customElement('campus-component-student-selection')
export class CampusComponentStudentSelection extends ChemistryLitElement {
  @state() public checkedStudentIDs = new RXController(this, [] as string[]);
  @state() public userType = new RXController<GroupType>(this, GroupType.STUDENTS);

  @state() private readonly _userFilter = new ChancenwerkUserFilter({
    search: '',
    searchFields: ['vorname', 'nachname'],
    aspects: [
      new BooleanAspect({
        customID: 'active',
        value: true,
      }),
    ],
  });
  @state() private readonly _studentFilter = new StudentFilter({
    search: '',
    searchFields: ['vorname', 'nachname'],
    aspects: [
      new BooleanAspect({
        customID: 'active',
        value: true,
      }),
    ],
  });
  private readonly _showOnlyMarked = new RXController<boolean>(this, false);

  private _getColumnDefinitions() {
    return [
      {
        columnName: 'Name',
        fieldName: 'name',
        columnWidth: '1fr',
        getFieldMapping: entity => {
          return entity.toString();
        },
        selectionSet: {
          vorname: 1,
          nachname: 1,
          email: 1,
        },
      },
      ...(this.userType.value === GroupType.STUDENTS
        ? [
            {
              columnName: 'Stufe',
              fieldName: 'schoolInformation.jahrgangsstufe',
              columnWidth: '1fr',
              getFieldMapping: entity => {
                return entity.schoolInformation?.jahrgangsstufe;
              },
              selectionSet: {
                schoolInformation: {
                  jahrgangsstufe: 1,
                },
              },
            },
          ]
        : []),
      {
        columnName: '',
        columnWidth: 'max-content',
        customID: 'action',
        renderItem: entity => {
          return html`
            <cr-flex center crossaxis-end>
              <cr-button
                mode="fab"
                icon="${this._isChecked(entity) ? 'check' : 'add'}"
                tone="${this._isChecked(entity) ? 'checked' : ''}"
                small
                @click=${async () => {
                  const event = new CustomEvent('value-picked', {
                    detail: {
                      value: entity,
                    },
                  });
                  this.dispatchEvent(event);
                }}
              >
              </cr-button>
            </cr-flex>
          `;
        },
      },
    ] as Array<
      ColumnDefinition<
        | typeof ChancenwerkUser
        | typeof Student
        | typeof Mitarbeiter
        | typeof ExternerMitarbeiter
        | typeof VerwaltungsMitarbeiter
      >
    >;
  }

  override styles() {
    return [
      {
        ':host': {
          height: '100%',
          width: '100%',
        },
        '.student': {
          userSelect: 'none',
          width: '100%',
          marginBottom: this.spacing.sm,
          background: `linear-gradient(90deg, ${this.colors.primary} 0%, ${this.colors.primary} 33%, ${this.colors.white} 66%, ${this.colors.white} 100%)`,
          backgroundSize: '300% 100%',
          backgroundPosition: '100% 0',
          transition: 'all .25s ease-in',
          cursor: 'pointer',
        },
        '.student:hover': {
          transform: 'scale(0.98,0.98)',
        },
        '.student:active': {
          transform: 'scale(0.95,0.95)',
        },
        '.marked': {
          backgroundPosition: '0% 0',
        },
      },
    ];
  }

  private _buildFilter() {
    if (!this._showOnlyMarked.value) {
      if (this._userFilter.ids?.length) {
        this._userFilter.ids = null;
      }
      if (this._studentFilter.ids?.length) {
        this._studentFilter.ids = null;
      }
    } else {
      this._userFilter.ids = this.checkedStudentIDs.value;
      this._studentFilter.ids = this.checkedStudentIDs.value;
    }

    if (this._userFilter.search === undefined) {
      this._userFilter.search = '';
    }

    return this.userType.value === GroupType.STUDENTS ? this._studentFilter : this._userFilter;
  }

  override render() {
    return html`
      <cr-flex space="sm" w100 h100>
        <cr-flex
          space="xs"
          ${css({
            position: 'sticky',
            top: '0',
            zIndex: '1',
          })}
        >
          <chancen-component-card-title>
            ${this.userType.value === GroupType.STUDENTS ? 'Schüler:innen zuweisen' : 'Mitarbeitende zuweisen'}
          </chancen-component-card-title>
          <cr-input
            ${css({ selfAlign: 'center', justifySelf: 'center', background: 'white' })}
            @value-changed=${(e: ValueEvent<string>) => {
              if (this._userFilter.search !== e.detail.value) this._userFilter.search = e.detail.value;
              if (this._studentFilter.search !== e.detail.value) this._studentFilter.search = e.detail.value;
              this.requestUpdate();
            }}
            placeholder="Suche"
            icon="search"
            flex
          ></cr-input>
          <cr-checkbox
            label="Nur hinzugefügte Anzeigen"
            @value-picked=${(e: ValueEvent<boolean>) => {
              this._showOnlyMarked.next(e.detail.value);
            }}
          ></cr-checkbox>
        </cr-flex>
        <x-data-view
          flex
          .options=${{
            class: this.userType.value === GroupType.STUDENTS ? Student : Mitarbeiter,
            view: new View({
              filter: this._buildFilter(),
            }),
            operation: (skip, limit) => {
              return UserAPI.subscribeByFilter(skip, limit, this._buildFilter());
            },
            loader: () =>
              html`<cr-flex w100 h100 center crossaxis-center><cr-campus-loader></cr-campus-loader></cr-flex>`,
            onlyShowLastTranslation: true,
            customColumns: this._getColumnDefinitions(),
          } satisfies DataViewOptions<typeof ChancenwerkUser>}
          ${css({ overflowY: 'auto', overflowX: 'hidden' })}
        >
        </x-data-view>
      </cr-flex>
    `;
  }

  private _isChecked(student: ChancenwerkUser): boolean {
    return this.checkedStudentIDs.value.includes(student._id);
  }
}
